import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import type { AssistantPlan } from '@bizzdesign/ai-assistant/data-access';

type PlanStepState = 'done' | 'active' | 'failed' | 'pending';

type PlanStep = {
  readonly label: string;
  readonly state: PlanStepState;
  /** Text equivalent of the state icon, which is hidden from assistive tech. */
  readonly stateLabel: string;
};

const PLAN_STEP_STATE_LABELS: Record<PlanStepState, string> = {
  done: 'Done:',
  active: 'In progress:',
  failed: 'Failed:',
  pending: 'Not started:',
};

/**
 * Renders the assistant's plan for a multi-phase task as a checklist: finished
 * phases ticked, the one in progress active, the rest pending.
 *
 * @remarks Presentational. The plan spans a chain of turns rather than one turn,
 * so it stays on screen while the user is moved to another page.
 */
@Component({
  selector: 'bd-ai-assistant-plan',
  standalone: true,
  templateUrl: './ai-assistant-plan.component.html',
  styleUrls: ['./ai-assistant-plan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantPlanComponent {
  readonly plan = input.required<AssistantPlan>();

  protected readonly steps = computed((): PlanStep[] => {
    const { steps, currentStep, failedStep, completed } = this.plan();
    // Steps finished under an earlier plan stay at the top: the user watched
    // them complete, so replacing the plan must not appear to undo them.
    return [
      ...completed.map((label) => this.planStep(label, 'done')),
      ...steps.map((label, index) =>
        this.planStep(label, this.stepState(index, currentStep, failedStep)),
      ),
    ];
  });

  private planStep(label: string, state: PlanStepState): PlanStep {
    return { label, state, stateLabel: PLAN_STEP_STATE_LABELS[state] };
  }

  private stepState(
    index: number,
    currentStep: number,
    failedStep: number | null,
  ): PlanStepState {
    if (failedStep !== null) {
      if (index === failedStep) return 'failed';
      return index < failedStep ? 'done' : 'pending';
    }
    if (index < currentStep) return 'done';
    return index === currentStep ? 'active' : 'pending';
  }
}
